import { useTranslation } from 'react-i18next';
import { Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useMetricTypesList } from '~/hooks/use-metric-types';
import { OperandSelect } from './operand-select';
import { OP_BETWEEN, metricOperands } from '~/lib/filters/operands';
import type { FilterNodeValue } from '~/lib/filters/registry';

/** One `metricTypes` condition, wire shape of `MetricTypeCondition` (Data/Criteria/Products/MetricTypesCriteria.cs). */
interface MetricCondition {
	metricTypeId?: number;
	op?: string;
	value?: number;
	valueTo?: number;
}

const toNumber = (raw: string) => (raw === '' ? undefined : Number(raw));

/** `metricTypes` leaf: a list of OR-ed conditions, each on one metric type. */
export function MetricTypesLeaf({ value, onChange }: { value: FilterNodeValue; onChange: (v: FilterNodeValue) => void }) {
	const { t } = useTranslation();
	const metricTypes = useMetricTypesList();
	const conditions = (Array.isArray(value.conditions) ? value.conditions : []) as MetricCondition[];
	const setConditions = (next: MetricCondition[]) => onChange({ ...value, conditions: next });
	const patch = (index: number, change: Partial<MetricCondition>) =>
		setConditions(conditions.map((c, i) => (i === index ? { ...c, ...change } : c)));

	return (
		<div className="flex flex-1 flex-col gap-2">
			{conditions.map((condition, index) => (
				<div key={index} className="flex flex-wrap items-center gap-2">
					<Select
						value={condition.metricTypeId != null ? String(condition.metricTypeId) : undefined}
						onValueChange={id => patch(index, { metricTypeId: Number(id) })}>
						<SelectTrigger className="h-9 w-40">
							<SelectValue placeholder={t('catalog.filters.metricType')} />
						</SelectTrigger>
						<SelectContent>
							{(metricTypes.data ?? []).map(mt => (
								<SelectItem key={mt.id} value={String(mt.id)}>
									{mt.nm}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<OperandSelect
						value={condition.op}
						onChange={op => patch(index, { op, valueTo: op === OP_BETWEEN ? condition.valueTo : undefined })}
						operands={metricOperands}
						invalid={condition.metricTypeId != null && !condition.op}
					/>
					<Input
						type="number"
						step="any"
						className="h-9 w-24"
						placeholder={condition.op === OP_BETWEEN ? t('catalog.filters.from') : t('catalog.filters.value')}
						value={condition.value ?? ''}
						onChange={e => patch(index, { value: toNumber(e.target.value) })}
					/>
					{condition.op === OP_BETWEEN && (
						<Input
							type="number"
							step="any"
							className="h-9 w-24"
							placeholder={t('catalog.filters.to')}
							value={condition.valueTo ?? ''}
							onChange={e => patch(index, { valueTo: toNumber(e.target.value) })}
						/>
					)}
					<Button
						type="button"
						variant="ghost"
						size="icon"
						className="h-7 w-7 text-danger-fg"
						onClick={() => setConditions(conditions.filter((_, i) => i !== index))}
						aria-label={t('common.remove')}>
						<X size={14} />
					</Button>
				</div>
			))}
			<div>
				<Button type="button" variant="outline" size="sm" onClick={() => setConditions([...conditions, {}])}>
					<Plus size={14} />
					{t('catalog.filters.addMetricCondition')}
				</Button>
			</div>
		</div>
	);
}
